import { useState } from 'react';
import {
  ThumbUp as AcceptIcon,
  ThumbDown as DeclineIcon,
  AutoAwesome as SuggestionIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Divider,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { SuggestedDocumentReferenceProps } from './SuggestedDocumentReference.props';

const CLASSIFICATION_DISPLAY_MAP: Record<string, string> = {
  CAP_TABLE: 'Cap Table',
  BALANCE_SHEET: 'Balance Sheet',
  INCOME_STATEMENT: 'Income Statement',
  CASH_FLOW_STATEMENT: 'Cash Flow Statement',
  OTHER: 'Other',
};

const SuggestedDocumentReference = ({
  documentReference,
  onAccept,
  onDecline,
}: SuggestedDocumentReferenceProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccept = () => {
    handleClose();
    onAccept();
  };

  const handleDecline = () => {
    handleClose();
    onDecline();
  };

  const open = Boolean(anchorEl);
  const id = open ? `suggestion-popover-${documentReference.document_reference_id}` : undefined;

  const classification = documentReference.ai_classification;
  const classificationResult = classification?.classification_result || 'Document';
  const displayName = CLASSIFICATION_DISPLAY_MAP[classificationResult] || classificationResult;
  
  // Get the suggested reference type from referenced_in if available
  const suggestedReference = documentReference.referenced_in?.[0];
  const referenceType = suggestedReference?.reference_object_type || displayName;

  return (
    <>
      <Chip
        aria-describedby={id}
        label={referenceType}
        onClick={handleClick}
        size="small"
        sx={{
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          border: '2px dashed #1976d2',
          color: '#1976d2',
          fontWeight: 500,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.15)',
          },
        }}
      />

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              p: 2,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            },
          },
        }}
      >
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SuggestionIcon sx={{ color: '#1976d2', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Suggested Reference
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add this{' '}
              <Typography
                component="span"
                variant="body2"
                color="primary"
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                Document
              </Typography>{' '}
              to{' '}
              <Typography
                component="span"
                variant="body2"
                color="primary"
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                {referenceType}
              </Typography>
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Commentary / Why this suggestion */}
        {classification?.commentary && classification.commentary.length > 0 && (
          <Box mb={2}>
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              Why this suggestion?
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
              {classification.commentary.map((note, index) => (
                <Typography
                  key={index}
                  component="li"
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}
                >
                  {note.content}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {/* Confidence indicator */}
        {classification?.confidence != null && (
          <Box mb={2}>
            <Typography variant="caption" color="text.secondary">
              Confidence: {Math.round(classification.confidence * 100)}%
            </Typography>
          </Box>
        )}

        {/* Action buttons */}
        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button
            variant="text"
            size="small"
            onClick={handleAccept}
            startIcon={<AcceptIcon sx={{ fontSize: 16 }} />}
            sx={{
              color: '#1976d2',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Accept this Suggestion
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={handleDecline}
            startIcon={<DeclineIcon sx={{ fontSize: 16 }} />}
            sx={{
              color: '#d32f2f',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Dismiss
          </Button>
        </Stack>
      </Popover>
    </>
  );
};

export default SuggestedDocumentReference;
